import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";

interface DeleteConfirmationProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, description: string) => void;
  adminName?: string;
  loading?: boolean;
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({
  open,
  onClose,
  onConfirm,
  adminName,
  loading = false,
}) => {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const handleConfirm = () => {
    setReasonError(false);
    setDescriptionError(false);

    if (!reason) {
      setReasonError(true);
      return;
    }

    if (!description.trim()) {
      setDescriptionError(true);
      return;
    }

    onConfirm(reason, description);
  };

  const handleClose = () => {
    if (!loading) {
      setReason("");
      setDescription("");
      setReasonError(false);
      setDescriptionError(false);
      onClose();
    }
  };

  const archiveReasons = [
    { value: "DUPLICATE", label: "Duplicate" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "DATA_CLEANUP", label: "Data Cleanup" },
    { value: "USER_REQUEST", label: "User Request" },
    { value: "COMPLIANCE", label: "Compliance" },
    { value: "OTHER", label: "Other" },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" color="error">
          Delete Admin
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete the admin{" "}
            <strong>{adminName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will archive the admin. The admin will be moved to the
            Archives section and can be restored later if needed.
          </Typography>
        </Box>

        <FormControl fullWidth margin="normal" error={reasonError}>
          <InputLabel>Reason for Deletion *</InputLabel>
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            label="Reason for Deletion *"
            disabled={loading}
          >
            {archiveReasons.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {reasonError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              Please select a reason for deletion
            </Typography>
          )}
        </FormControl>

        <TextField
          autoFocus
          margin="normal"
          label="Description *"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={descriptionError}
          helperText={
            descriptionError
              ? "Please provide a description"
              : "Provide additional details about why this admin is being deleted"
          }
          disabled={loading}
          placeholder="e.g., Duplicate account created by mistake, Admin requested account removal, etc."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          sx={{
            textTransform: "none",
            minWidth: 100,
          }}
        >
          {loading ? "Deleting..." : "Delete Admin"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmation; 